/**
 * Course Labs Page - List labs for a specific course with Pagination & Search
 * Enhanced with progress tracking and reset functionality
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiService } from '../services/api';
import { useCurrency } from '../hooks/useCurrency';
import { COURSE_DATA } from '../constants/courseData';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Clock, Award, ChevronLeft, Play, Search, ChevronRight, CheckCircle, RotateCcw, AlertCircle, CreditCard, Lock } from 'lucide-react';
import SEO from '../components/SEO';
import { trackCourseView } from '../utils/metaPixel';

const CourseLabs = () => {
  const { course } = useParams();
  const { isIndia } = useCurrency();

  const COURSE_LOGOS = {
    RH124: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/redhat/redhat-original.svg',
    RH134: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/redhat/redhat-original.svg',
    SVRADM: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/linux/linux-original.svg',
    ANSIBLE: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/ansible/ansible-original.svg',
    DOCKER: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg',
    TERRAFORM: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/terraform/terraform-original.svg',
    GIT: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/git/git-original.svg',
    PYTHON: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg',
    C_LANG: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/c/c-original.svg',
    CPP: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/cplusplus/cplusplus-original.svg',
    MYSQL: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mysql/mysql-original.svg',
    JAVA: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/java/java-original.svg',
    KUBERNETES: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/kubernetes/kubernetes-plain.svg',
  };

  const COURSE_TAGLINES = {
    RH124: 'Master Linux fundamentals with real hands-on labs',
    RH134: 'Advanced Linux system administration with practical exercises',
    SVRADM: 'Server administration essentials for production environments',
    ANSIBLE: 'Automate infrastructure with Ansible playbooks & roles',
    DOCKER: 'Build, ship & run containers with hands-on Docker labs',
    TERRAFORM: 'Infrastructure as Code — provision cloud resources hands-on',
    GIT: 'Master version control with real Git workflows & branching',
    PYTHON: 'Learn Python programming from basics to advanced concepts',
    C_LANG: 'Master C programming with structured hands-on exercises',
    CPP: 'Object-oriented programming with C++ practical labs',
    MYSQL: 'Master MySQL/MariaDB database administration hands-on',
    JAVA: 'Core Java from zero to hero — OOP, Collections, JDBC & more',
  };

  const courseLogo = COURSE_LOGOS[course];
  const courseTagline = COURSE_TAGLINES[course] || `Master ${course} with hands-on exercises`;
  const [labs, setLabs] = useState([]);
  const [userProgress, setUserProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [resetting, setResetting] = useState({});
  const [isEnrolled, setIsEnrolled] = useState(null);
  const [courseAccess, setCourseAccess] = useState(null);
  const [allCourseAccess, setAllCourseAccess] = useState({});
  const labsPerPage = 12;
  
  // Get course price from data
  const getCoursePrice = (code) => {
    const courseData = COURSE_DATA[code];
    if (!courseData) return isIndia ? '999' : '45';
    return isIndia 
      ? courseData.price.toLocaleString('en-IN')
      : courseData.priceUSD?.toString() || '19';
  };
  
  const formatPrice = (code) => {
    const courseData = COURSE_DATA[code];
    if (!courseData) return isIndia ? '₹999' : '$45';
    return isIndia 
      ? `₹${courseData.price.toLocaleString('en-IN')}`
      : `$${courseData.priceUSD || 19}`;
  };

  useEffect(() => {
    fetchCourseLabs();
  }, [course]);

  // Meta Pixel: ViewContent — fires when a course detail page is opened.
  // Used by Meta Ads Manager to build interest-based lookalike audiences.
  // Re-fires on course change (different course = different content view).
  useEffect(() => {
    if (course) {
      const meta = COURSE_DATA[course];
      trackCourseView(course, meta?.fullName || meta?.name || course);
    }
  }, [course]);

  useEffect(() => {
    // Reset to first page when search term changes
    setCurrentPage(1);
  }, [searchTerm]);

  // Restore page context when returning from lab
  useEffect(() => {
    try {
      const savedContext = localStorage.getItem('coursepage_context');
      if (savedContext) {
        const context = JSON.parse(savedContext);
        
        // Check if context is recent (within 1 hour) and for same course
        const isRecent = Date.now() - context.timestamp < 3600000; // 1 hour
        const isSameCourse = context.course === course;
        
        if (isRecent && isSameCourse && context.page) {
          setCurrentPage(context.page);
          // Clear the context after restoration to avoid auto-restoration on refresh
          localStorage.removeItem('coursepage_context');
        }
      }
    } catch (error) {
      console.log('Page context restoration error:', error);
    }
  }, [course]);

  const fetchCourseLabs = async () => {
    try {
      // Fetch lab list first (required)
      const labsData = await apiService.getCourseLabs(course);
      setLabs(labsData);
      
      // Fetch course access - THIS IS CRITICAL for determining premium status
      let courseAccessData = { user_access: null };
      try {
        courseAccessData = await apiService.getCourseAccess(course);
      } catch (accessError) {
        // Retry once on failure
        try {
          await new Promise(resolve => setTimeout(resolve, 500));
          courseAccessData = await apiService.getCourseAccess(course);
        } catch (retryError) {
          // Still failed - use default but log for debugging
        }
      }
      
      // Fetch other optional data
      let allProgressData = [];
      let enrollmentData = { enrolled: false };
      
      try {
        allProgressData = await apiService.getMyProgress();
      } catch (progressError) {
        // Non-critical - continue without progress data
      }
      
      try {
        enrollmentData = await apiService.checkCourseEnrollment(course);
      } catch (enrollError) {
        // Non-critical - continue with default enrollment status
      }
      
      // Fetch all course access with individual catch
      const [rh124Access, rh134Access, svradmAccess, ansibleAccess, dockerAccess, terraformAccess, kubernetesAccess, gitAccess, pythonAccess, cLangAccess, cppAccess] = await Promise.all([
        apiService.getCourseAccess('RH124').catch(() => ({ user_access: null })),
        apiService.getCourseAccess('RH134').catch(() => ({ user_access: null })),
        apiService.getCourseAccess('SVRADM').catch(() => ({ user_access: null })),
        apiService.getCourseAccess('ANSIBLE').catch(() => ({ user_access: null })),
        apiService.getCourseAccess('DOCKER').catch(() => ({ user_access: null })),
        apiService.getCourseAccess('TERRAFORM').catch(() => ({ user_access: null })),
        apiService.getCourseAccess('KUBERNETES').catch(() => ({ user_access: null })),
        apiService.getCourseAccess('GIT').catch(() => ({ user_access: null })),
        apiService.getCourseAccess('PYTHON').catch(() => ({ user_access: null })),
        apiService.getCourseAccess('C_LANG').catch(() => ({ user_access: null })),
        apiService.getCourseAccess('CPP').catch(() => ({ user_access: null }))
      ]);
      
      // Filter progress data for current course on frontend
      const courseProgressData = allProgressData.filter(p => p.course === course);
      
      setUserProgress(courseProgressData || []);
      setIsEnrolled(enrollmentData.enrolled);
      setCourseAccess(courseAccessData);
      setAllCourseAccess({
        RH124: rh124Access,
        RH134: rh134Access,
        SVRADM: svradmAccess,
        ANSIBLE: ansibleAccess,
        DOCKER: dockerAccess,
        TERRAFORM: terraformAccess,
        KUBERNETES: kubernetesAccess,
        GIT: gitAccess,
        PYTHON: pythonAccess,
        C_LANG: cLangAccess,
        CPP: cppAccess
      });
    } catch (error) {
      setError('Unable to load course content. Please check your connection and try refreshing the page.');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get lab completion status
  const getLabStatus = (labId) => {
    const labProgress = userProgress.find(p => p.lab_id === labId);
    if (!labProgress) return { status: 'not_started', completed: 0, total: 0 };
    
    const lab = labs.find(l => l.id === labId);
    const totalTasks = lab?.tasks?.length || 0;
    const completedTasks = labProgress.completed_tasks || 0; // Fix: completed_tasks is integer, not array
    
    if (completedTasks === 0) return { status: 'not_started', completed: 0, total: totalTasks };
    if (completedTasks === totalTasks) return { status: 'completed', completed: completedTasks, total: totalTasks };
    return { status: 'in_progress', completed: completedTasks, total: totalTasks };
  };

  // Reset lab progress
  const handleResetLab = async (labId) => {
    try {
      setResetting(prev => ({ ...prev, [labId]: true }));
      
      // Show confirmation dialog
      const confirmed = window.confirm(
        `Are you sure you want to reset progress for this lab? All completed tasks will be marked as incomplete.`
      );
      
      if (!confirmed) return;
      
      // Call reset API
      await apiService.resetLabProgress(course, labId);
      
      // Refresh data
      await fetchCourseLabs();
      
    } catch (error) {
      console.error('Failed to reset lab:', error);
      alert('Failed to reset lab progress. Please try again.');
    } finally {
      setResetting(prev => ({ ...prev, [labId]: false }));
    }
  };

  // Filter labs based on search term
  const filteredLabs = useMemo(() => {
    if (!searchTerm.trim()) return labs;
    
    return labs.filter(lab => 
      lab.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lab.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lab.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [labs, searchTerm]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredLabs.length / labsPerPage);
  const startIndex = (currentPage - 1) * labsPerPage;
  const currentLabs = filteredLabs.slice(startIndex, startIndex + labsPerPage);

  // Pagination handlers
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-24 bg-slate-200 rounded-2xl"></div>
            <div className="space-y-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-32 bg-slate-200 rounded-xl"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6 flex items-center justify-center">
        <Card className="bg-white border border-red-200 max-w-md shadow-lg rounded-xl">
          <CardContent className="p-6 text-center">
            <div className="bg-red-100 p-3 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <span className="text-red-600 text-2xl">⚠️</span>
            </div>
            <p className="text-red-700 mb-4">{error}</p>
            <Button 
              onClick={fetchCourseLabs} 
              className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-lg"
            >
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      <SEO 
        title={`${course} Labs - DevOps Training | Grras Solutions`}
        description={`Master ${course} through hands-on DevOps laboratories. Practice Docker containerization, Kubernetes orchestration, Ansible automation, and Linux administration in real-world scenarios.`}
        keywords={`${course}, DevOps labs, Docker training, Kubernetes labs, Ansible automation, Linux containerization, ${course} exercises, infrastructure management`}
      />
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100" data-testid="course-labs-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Mobile-Optimized Header */}
        <div className="bg-gradient-to-r from-blue-900 via-blue-800 to-indigo-900 rounded-xl p-4 sm:p-5 mb-6 shadow-xl">
          <div className="flex items-center gap-3 sm:gap-4">
            <Link 
              to="/courses"
              className="bg-white/20 backdrop-blur-sm p-2 rounded-xl text-blue-100 hover:bg-white/30 transition-all duration-300 min-h-[44px] min-w-[44px] flex items-center justify-center touch-manipulation"
              data-testid="back-to-courses"
            >
              <ChevronLeft className="h-5 w-5" />
            </Link>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 sm:gap-3 mb-2">
                <div className="bg-gradient-to-r from-emerald-400 to-cyan-400 p-2 rounded-xl flex-shrink-0">
                  {courseLogo ? (
                    <img src={courseLogo} alt={course} className="h-5 w-5 sm:h-6 sm:w-6" />
                  ) : (
                    <Award className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                  )}
                </div>
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white truncate">
                  {course} Labs
                </h1>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <p className="text-blue-200 text-sm sm:text-base mb-2 sm:mb-0">
                  {courseTagline}
                </p>
                
                {/* Course Access Status */}
                {courseAccess && (
                  <div className="flex items-center space-x-2">
                    {courseAccess.user_access?.access_type === 'premium' ? (
                      <div className="bg-emerald-500/20 border border-emerald-400/30 text-emerald-200 px-3 py-1 rounded-full text-xs font-medium">
                        ✅ Premium Access • Full Course
                      </div>
                    ) : (
                      <>
                        <div className="bg-blue-500/20 border border-blue-400/30 text-blue-200 px-3 py-1 rounded-full text-xs font-medium">
                          ✓ Open Access: {courseAccess.user_access?.accessible_labs || 0} labs
                        </div>
                        {courseAccess.user_access?.premium_labs_locked > 0 && (
                          <div className="bg-orange-500/20 border border-orange-400/30 text-orange-200 px-3 py-1 rounded-full text-xs font-medium">
                            🔒 {courseAccess.user_access.premium_labs_locked} premium labs
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Search Bar & Stats Row - Mobile Enhanced */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          
          {/* Premium Unlock Banner - Shows only for non-premium users with locked labs */}
          {/* Special handling for EXAM_RHCSA - redirect to exam page instead */}
          {course === 'EXAM_RHCSA' && courseAccess && courseAccess.user_access?.access_type !== 'premium' && (
            <div className="w-full mb-4 bg-gradient-to-r from-red-50 via-red-100 to-orange-50 border border-red-200 rounded-xl p-4 shadow-sm">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="bg-gradient-to-br from-red-600 to-red-700 p-2.5 rounded-xl shadow-md">
                    <Award className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">
                      🎯 RHCSA Exam Practice - 3 Complete Exam Sets
                    </h3>
                    <p className="text-sm text-gray-600">
                      45 real exam questions with auto verification
                    </p>
                  </div>
                </div>
                <Link 
                  to="/payment/EXAM_RHCSA"
                  className="flex items-center gap-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-5 py-2.5 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-300 whitespace-nowrap"
                >
                  <CreditCard className="h-4 w-4" />
                  Unlock @ {formatPrice('EXAM_RHCSA')}
                </Link>
              </div>
            </div>
          )}
          
          {/* Regular courses - Premium Unlock Banner */}
          {course !== 'EXAM_RHCSA' && courseAccess && courseAccess.user_access?.access_type !== 'premium' && courseAccess.user_access?.premium_labs_locked > 0 && (
            <div className="w-full mb-4 bg-gradient-to-r from-amber-50 via-orange-50 to-red-50 border border-orange-200 rounded-xl p-4 shadow-sm">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="bg-gradient-to-br from-orange-500 to-red-500 p-2.5 rounded-xl shadow-md">
                    <Lock className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">
                      🔒 {courseAccess.user_access.premium_labs_locked} Premium Labs Locked
                    </h3>
                    <p className="text-sm text-gray-600">
                      Unlock all {courseAccess.user_access.total_labs} labs with a single purchase
                    </p>
                  </div>
                </div>
                {(() => {
                  // Simplified pricing - no more upgrades, just direct course purchase
                  const paymentRoute = `/payment/${course}`;
                  const priceText = formatPrice(course);
                  
                  return (
                    <Link 
                      to={paymentRoute}
                      className="flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white px-5 py-2.5 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-300 whitespace-nowrap"
                    >
                      <CreditCard className="h-4 w-4" />
                      Unlock Full Course @ {priceText}
                    </Link>
                  );
                })()}
              </div>
            </div>
          )}
          
          <div className="relative w-full sm:max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-slate-400" />
            </div>
            <input
              type="text"
              placeholder="Search DevOps labs..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="block w-full pl-9 pr-3 py-3 sm:py-2.5 border border-slate-200 rounded-lg bg-white text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm text-base sm:text-sm touch-manipulation"
            />
          </div>

          {/* Enhanced Stats */}
          {!loading && !error && (
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="bg-white rounded-lg px-3 py-2 border border-slate-200 shadow-sm text-sm text-slate-600">
                <span>
                  Showing <span className="font-semibold text-blue-600">{currentLabs.length}</span> of <span className="font-semibold">{filteredLabs.length}</span> labs
                  {searchTerm && ` (filtered from ${labs.length})`}
                </span>
              </div>
              
              {/* Progress Summary */}
              {userProgress.length > 0 && (
                <div className="flex gap-2">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-sm">
                    <span className="text-emerald-700">
                      <CheckCircle className="inline h-3 w-3 mr-1" />
                      {userProgress.filter(p => {
                        const lab = labs.find(l => l.id === p.lab_id);
                        return lab && p.completed_tasks === lab.tasks?.length;
                      }).length} completed
                    </span>
                  </div>
                  
                  <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm">
                    <span className="text-amber-700">
                      <AlertCircle className="inline h-3 w-3 mr-1" />
                      {userProgress.filter(p => {
                        const lab = labs.find(l => l.id === p.lab_id);
                        return lab && p.completed_tasks > 0 && p.completed_tasks < lab.tasks?.length;
                      }).length} in progress
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Enhanced Labs Grid - Freemium Model */}
        {currentLabs.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
            {currentLabs.map((lab, index) => {
              const absoluteIndex = startIndex + index;
              const labStatus = getLabStatus(lab.id);
              const isResetting = resetting[lab.id];
              
              // Check if lab is free or premium
              // RH124: First 3 labs are free (lab1, lab2, lab3)
              // C_LANG: First 2 labs are free (lab1, lab2)
              // All other courses: No free labs
              const isFreeLab = (
                (course === 'RH124' && ['lab1', 'lab2', 'lab3'].includes(lab.id)) ||
                (course === 'C_LANG' && ['lab1', 'lab2'].includes(lab.id))
              );
              const isPremiumUser = courseAccess?.user_access?.access_type === 'premium';
              const canAccess = isFreeLab || isPremiumUser;
              
              return (
                <Card
                  key={lab.id}
                  className={`bg-white border shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 rounded-xl overflow-hidden ${
                    !canAccess ? 'border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 opacity-90' :
                    labStatus.status === 'completed' ? 'border-emerald-200 bg-gradient-to-br from-emerald-50 to-green-50' :
                    labStatus.status === 'in_progress' ? 'border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50' :
                    isFreeLab ? 'border-green-200 bg-gradient-to-br from-green-50 to-emerald-50' :
                    'border-slate-200 bg-gradient-to-br from-slate-50 to-gray-50'
                  }`}
                  data-testid={`lab-card-${lab.id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0 shadow-lg ${
                          labStatus.status === 'completed' ? 'bg-gradient-to-br from-emerald-500 to-emerald-600' :
                          labStatus.status === 'in_progress' ? 'bg-gradient-to-br from-blue-500 to-blue-600' :
                          isFreeLab ? 'bg-gradient-to-br from-green-500 to-green-600' :
                          'bg-gradient-to-br from-indigo-500 to-purple-600'
                        }`}>
                          {labStatus.status === 'completed' ? <CheckCircle className="h-4 w-4" /> : absoluteIndex + 1}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          {/* Lab Title - Full width */}
                          <CardTitle className="text-slate-900 text-base font-bold leading-tight mb-2">{lab.title}</CardTitle>
                          
                          {/* Badges Row */}
                          <div className="flex items-center gap-2 mb-3">
                            {/* Access Type Badge */}
                            {isFreeLab && (
                              <Badge className="bg-gradient-to-r from-green-100 to-green-50 text-green-700 text-xs px-2.5 py-1 font-medium border border-green-200">
                                ✓ Open Access
                              </Badge>
                            )}
                            {!canAccess && (
                              <Badge className="bg-gradient-to-r from-amber-100 to-amber-50 text-amber-700 text-xs px-2.5 py-1 font-medium border border-amber-200">
                                ⭐ Premium
                              </Badge>
                            )}
                            
                            {/* Status Badges */}
                            {canAccess && labStatus.status === 'completed' && (
                              <Badge className="bg-gradient-to-r from-emerald-100 to-emerald-50 text-emerald-700 text-xs px-2.5 py-1 font-medium border border-emerald-200">
                                ✓ Completed
                              </Badge>
                            )}
                            {canAccess && labStatus.status === 'in_progress' && (
                              <Badge className="bg-gradient-to-r from-blue-100 to-blue-50 text-blue-700 text-xs px-2.5 py-1 font-medium border border-blue-200">
                                ⏳ In Progress
                              </Badge>
                            )}
                          </div>
                          
                          {/* Tasks Progress Indicator */}
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className="bg-slate-100 text-slate-700 text-xs px-2.5 py-1 font-medium">
                              {labStatus.completed}/{labStatus.total || lab.tasks?.length || 0} tasks
                            </Badge>
                          </div>
                          
                          <CardDescription className="text-slate-700 text-sm mt-1.5 mb-3 line-clamp-2 leading-relaxed">
                            {lab.description || 'Complete the lab exercises to practice your skills.'}
                          </CardDescription>
                          
                          <div className="flex items-center gap-3 text-xs text-slate-500">
                            <div className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              <span>~30 min</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Award className="h-3 w-3" />
                              <span>{lab.tasks?.reduce((sum, task) => sum + (task.points || 0), 0) || 0} pts</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="ml-3 flex-shrink-0 flex gap-2">
                        {canAccess ? (
                          <>
                            {/* Reset Button - Touch Friendly */}
                            {(labStatus.status === 'completed' || labStatus.status === 'in_progress') && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleResetLab(lab.id)}
                                disabled={isResetting}
                                className="bg-white border-slate-200 text-slate-600 hover:bg-slate-50 px-3 py-2 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center touch-manipulation"
                                title="Reset lab progress"
                                data-testid={`reset-lab-button-${lab.id}`}
                              >
                                {isResetting ? (
                                  <div className="h-3 w-3 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600"></div>
                                ) : (
                                  <RotateCcw className="h-3 w-3" />
                                )}
                              </Button>
                            )}
                            
                            {/* Start/Continue Button - Touch Friendly */}
                            <Button 
                              asChild 
                              size="sm"
                              className={`px-4 py-2 h-9 text-xs font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-105 ${
                                labStatus.status === 'completed' ? 'bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white' :
                                labStatus.status === 'in_progress' ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white' :
                                isFreeLab ? 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white' :
                                'bg-gradient-to-r from-indigo-600 to-purple-700 hover:from-indigo-700 hover:to-purple-800 text-white'
                              }`}
                              data-testid={`start-lab-button-${lab.id}`}
                            >
                              <Link 
                                to={`/labs/${course}/${lab.id}`}
                                onClick={() => {
                                  // Store current page context for smart navigation
                                  localStorage.setItem('coursepage_context', JSON.stringify({
                                    course: course,
                                    page: currentPage,
                                    totalPages: totalPages,
                                    timestamp: Date.now()
                                  }));
                                }}
                              >
                                <div className="flex items-center">
                                  {labStatus.status === 'completed' ? (
                                    <>
                                      <CheckCircle className="mr-1.5 h-3 w-3" />
                                      Review
                                    </>
                                  ) : labStatus.status === 'in_progress' ? (
                                    <>
                                      <Play className="mr-1.5 h-3 w-3" />
                                      Continue
                                    </>
                                  ) : (
                                    <>
                                      <Play className="mr-1.5 h-3 w-3" />
                                      {isFreeLab ? 'Start Lab' : 'Start Lab'}
                                    </>
                                  )}
                                </div>
                              </Link>
                            </Button>
                          </>
                        ) : (
                          /* Upgrade Button for Locked Premium Labs */
                          <Button 
                            asChild 
                            size="sm"
                            className="px-4 py-2 rounded-lg font-medium shadow-md hover:shadow-lg transition-all duration-300 min-h-[44px] touch-manipulation bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white"
                            data-testid={`upgrade-button-${lab.id}`}
                          >
                            <Link to={`/payment/${course}`}>
                              <div className="flex items-center">
                                🔒 Premium
                              </div>
                            </Link>
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="bg-white rounded-2xl p-12 max-w-md mx-auto border border-slate-200 shadow-lg">
              {searchTerm ? (
                <>
                  <Search className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                  <h2 className="text-xl font-semibold text-slate-700 mb-2">No labs found</h2>
                  <p className="text-slate-500 mb-4">
                    Try searching with different keywords
                  </p>
                  <button 
                    onClick={() => setSearchTerm('')}
                    className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-4 py-2 rounded-lg font-medium transition-all duration-300"
                  >
                    Clear search
                  </button>
                </>
              ) : (
                <>
                  <Award className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                  <h2 className="text-xl font-semibold text-slate-700 mb-2">No labs available</h2>
                  <p className="text-slate-500">Labs for this course are coming soon</p>
                </>
              )}
            </div>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="text-sm text-slate-600">
                Page <span className="font-semibold text-blue-600">{currentPage}</span> of <span className="font-semibold">{totalPages}</span>
              </div>
              
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100 disabled:opacity-50 rounded-lg"
                >
                  Previous
                </Button>
                
                {/* Page Numbers */}
                <div className="flex items-center gap-1">
                  {[...Array(Math.min(totalPages, 7))].map((_, i) => {
                    let pageNum;
                    if (totalPages <= 7) {
                      pageNum = i + 1;
                    } else {
                      // Smart pagination logic for many pages
                      if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 6 + i;
                      } else {
                        pageNum = currentPage - 3 + i;
                      }
                    }
                    
                    return (
                      <Button
                        key={pageNum}
                        variant="outline"
                        size="sm"
                        onClick={() => goToPage(pageNum)}
                        className={`w-8 h-8 p-0 rounded-lg ${
                          currentPage === pageNum
                            ? 'bg-gradient-to-r from-blue-600 to-blue-700 border-blue-600 text-white shadow-lg'
                            : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                        }`}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100 disabled:opacity-50 rounded-lg"
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default CourseLabs;